import { useUncontrolled } from '@byecode/ui/hooks/useUncontrolled'
import { useByecodeUIConfig } from '@byecode/ui/providers'
import { formatMessage } from '@byecode/ui/utils/intl'
import clsx from 'clsx'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'

import { Box } from '../Box'
import { Empty } from '../Empty'
import { IconFont } from '../IconFont'
import { Input } from '../Input'
import { Text } from '../Text'
import { useStyles } from './CascadeList.styles'
import type { CascadeListProps, CascadeOption } from './CascadeList.types'

const emptyArr: CascadeOption[] = []

function getChildOption(option: CascadeOption, list: CascadeOption[]) {
    const { value, label, path, labelPath, children = [] } = option
    let arrList = list
    for (const child of children) {
        const newChild = {
            ...child,
            path: `${path}/${child.value}`,
            labelPath: `${labelPath}/${child.label}`,
            isLast: (child.children ?? []).length === 0
        }
        arrList = [...arrList, newChild, ...getChildOption(newChild, [])]
    }
    return arrList
}

// 返回 option 下所有最终层级（叶子节点）的子项（不包括自身，除非它本身就是叶子）
export function getLeafNodes(option: CascadeOption): CascadeOption[] {
    const allChildren = getChildOption(option, [])
    return allChildren.filter(opt => opt.isLast)
}

export const ALL_CASCADE_OPTION: CascadeOption = {
    value: 'ALL',
    label: '全部'
}

export const CascadeList = React.forwardRef<HTMLDivElement, CascadeListProps>(
    (
        {
            data,
            options = emptyArr,
            columnWidth = 212,
            selectionMode = true,
            multiple = false,
            searchPlaceholder = '搜索',
            searchable,
            classNames,
            unstyled,
            tabIndex,
            showPath,
            allSelected,
            className,
            emptyProps,
            styles,
            style,
            itemChildrenComponent,
            onPathChange,
            onChange
        },
        ref
    ) => {
        const { locale } = useByecodeUIConfig()
        const cursorRef = useRef<HTMLInputElement>(null)
        const columnsRef = useRef<HTMLDivElement>(null)
        const [keywords, setKeywords] = useState('')
        const [_value = [], _Change] = useUncontrolled({ value: data, onChange })
        const defaultOptions = useMemo(() => [options], [options])

        const LANGUAGE_ALL_CASCADE_OPTION = useMemo(() => ({ ...ALL_CASCADE_OPTION, label: formatMessage('ALL', locale) }), [locale])

        const [list, setList] = useState(defaultOptions)

        const { classes } = useStyles(
            { columnWidth: keywords ? '100%' : columnWidth },
            { name: 'CascadeList', classNames, styles, unstyled }
        )

        const flatOptions = useMemo(() => {
            return defaultOptions[0].reduce<CascadeOption[]>((pre, cur) => {
                const newOption = { ...cur, path: cur.value, labelPath: cur.label, isLast: (cur.children ?? []).length === 0 }
                return [...pre, newOption, ...getChildOption(newOption, [])]
            }, [])
        }, [defaultOptions])

        const isAllSelected = useMemo(() => {
            const nodes = selectionMode === 'any' ? flatOptions : flatOptions.filter(v => v.isLast)
            const nodeIds = nodes.map(v => v.value)
            return nodeIds.every(v => _value.includes(v))
        }, [_value, flatOptions, selectionMode])

        const filterList = useMemo(() => {
            switch (selectionMode) {
                case 'any': {
                    const newOptions = flatOptions.filter(
                        option => option.label?.includes(keywords) || option.labelPath?.includes(keywords)
                    )
                    return keywords === '' ? list : [newOptions]
                }
                case 'auto-leaf':
                case 'leaf-only': {
                    const newOptions = flatOptions.filter(
                        option => (option.labelPath?.includes(keywords) || option.label?.includes(keywords)) && option.isLast
                    )
                    return keywords === '' ? list : [newOptions]
                }
                default: {
                    return list
                }
            }
        }, [flatOptions, keywords, list, selectionMode])

        const valueOptionPath = useMemo(
            () =>
                _value
                    .map(v => {
                        return flatOptions.find(option => option.value === v)?.labelPath ?? ''
                    })
                    .join(','),
            [_value, flatOptions]
        )

        useEffect(() => {
            setList(defaultOptions)
        }, [defaultOptions])

        const handleSearchValueChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
            const word = ev.target.value.trim()
            setKeywords(word)
        }, [])

        const handleSelect = useCallback(
            (data: CascadeOption, index: number) => {
                // 从 flatOptions 中查找点击项（确保包含完整路径信息）
                const option = flatOptions.find(opt => opt.value === data.value)

                if (!option?.path) {
                    return
                }

                switch (selectionMode) {
                    case 'any': {
                        const newValue = new Set(_value)
                        const isChecked = newValue.has(data.value)
                        if (multiple) {
                            isChecked ? newValue.delete(data.value) : newValue.add(data.value)
                            _Change([...newValue])
                            break
                        }
                        isChecked && newValue.delete(data.value)
                        _Change(isChecked ? [] : [data.value])
                        break
                    }
                    case 'auto-leaf': {
                        const leafNodes = getLeafNodes(data)
                        const newValue = new Set([..._value, ...leafNodes.map(v => v.value)])
                        if (option.isLast) {
                            const isChecked = _value.includes(data.value)
                            if (multiple) {
                                isChecked ? newValue.delete(data.value) : newValue.add(data.value)
                                _Change([...newValue])
                                break
                            }
                            _Change(isChecked ? [] : [data.value])
                            break
                        }
                        const isChecked = leafNodes.every(v => _value.includes(v.value)) && leafNodes.length > 0
                        if (multiple) {
                            _Change(isChecked ? [...newValue].filter(v => !leafNodes.some(d => d.value === v)) : [...newValue])
                            break
                        }
                        _Change(isChecked ? [] : data.isLast ? [data.value] : [leafNodes[0].value])
                        break
                    }
                    case 'leaf-only': {
                        if (option.isLast) {
                            const newValue = new Set(_value)
                            const isChecked = newValue.has(data.value)
                            if (multiple) {
                                isChecked ? newValue.delete(data.value) : newValue.add(data.value)
                                _Change([...newValue])
                                break
                            }
                            isChecked && newValue.delete(data.value)
                            _Change(isChecked ? [] : [data.value])
                        }

                        break
                    }
                    default: {
                        break
                    }
                }
            },
            [_Change, _value, flatOptions, multiple, selectionMode]
        )

        const handleAllSelect = useCallback(() => {
            const nodes = selectionMode === 'any' ? flatOptions : flatOptions.filter(v => v.isLast)
            const nodeIds = nodes.map(v => v.value)
            _Change(isAllSelected ? [] : nodeIds)
        }, [_Change, flatOptions, isAllSelected, selectionMode])

        const handleScrollColumns = useCallback(
            (i: number) => {
                if (typeof columnWidth === 'number' && i !== -1) {
                    columnsRef?.current?.scrollBy({ left: columnWidth * i, behavior: 'smooth' })
                }
            },
            [columnWidth]
        )

        const handlePathSelect = useCallback(
            (data: CascadeOption, index: number, isSelected?: boolean) => {
                const option = flatOptions.find(option => {
                    return option.value === data.value
                })
                if (!option) {
                    return
                }
                setList(v => {
                    const newList = v.slice(0, index + 1)
                    return option.isLast ? newList : [...newList, option.children ?? []]
                })
                onPathChange?.(option)
                // 切换列时触发
                handleScrollColumns(index + 1)
                // 如果是最后 或者 不强制选择最后一级时始终调用handleSelect
                if (isSelected) {
                    handleSelect(option, index)
                }
            },
            [flatOptions, onPathChange, handleScrollColumns, handleSelect]
        )

        // 增加新列时触发
        useUpdateEffect(() => {
            handleScrollColumns(list.length)
        }, [list.length])

        return (
            <Box className={clsx(classes.root, className)} ref={ref} style={style} tabIndex={tabIndex}>
                <Box className={classes.content}>
                    {searchable && (
                        <Box className={classes.cursor}>
                            <Input
                                value={keywords}
                                placeholder={searchPlaceholder}
                                onChange={handleSearchValueChange}
                                ref={cursorRef}
                                prefix={<IconFont type="SearchLine" color="var(--color-gray-400)" size={16} />}
                                className={classes.search}
                                suffix={
                                    keywords && (
                                        <IconFont
                                            type="CloseCircle"
                                            color="var(--color-gray-400)"
                                            size={16}
                                            onClick={() => {
                                                setKeywords('')
                                                setList(defaultOptions)
                                            }}
                                        />
                                    )
                                }
                            />
                        </Box>
                    )}
                    {showPath && (
                        <Box classNames={classes.path}>
                            <Text color="var(--color-gray-400)" size={14}>
                                已选：{valueOptionPath}
                            </Text>{' '}
                        </Box>
                    )}
                    <Box className={classes.columns} ref={columnsRef}>
                        {filterList.map((columns, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Box className={classes.column} data-cascade-column={`columns-${index}`} key={index}>
                                <Box className={classes.list}>
                                    {multiple && allSelected && index === 0 && keywords === '' && (
                                        <Box className={clsx(classes.item, { active: true })} onClick={handleAllSelect}>
                                            <Box className={clsx(classes.leftIcon)}>
                                                {isAllSelected && (
                                                    <IconFont
                                                        type="Tick"
                                                        size={16}
                                                        className={classes.activeIcon}
                                                        color="var(--byecode-colors-colorPrimary)"
                                                    />
                                                )}
                                            </Box>
                                            <Text className={classes.label}>{LANGUAGE_ALL_CASCADE_OPTION.label}</Text>
                                        </Box>
                                    )}
                                    {columns.map(column => {
                                        // 如果该选项有子集，则计算其叶子节点，否则以自身作为叶子节点
                                        const leafNodes = column.children && column.children.length > 0 ? getLeafNodes(column) : [column]
                                        const leafValues = leafNodes.map(node => node.value)
                                        const allSelected = leafValues.every(val => _value.includes(val))
                                        const partiallySelected = !allSelected && leafValues.some(val => _value.includes(val))
                                        const checked = selectionMode === 'any' ? _value.includes(column.value) : allSelected
                                        return (
                                            <Box
                                                key={column.value}
                                                className={clsx(classes.item, { active: true, disabled: column.disabled })}
                                                onClick={() => handlePathSelect(column, index, !column.disabled)}
                                            >
                                                {itemChildrenComponent ? (
                                                    itemChildrenComponent?.(column, () => handlePathSelect(column, index))
                                                ) : (
                                                    <>
                                                        <Box className={clsx(classes.leftIcon)}>
                                                            {checked && (
                                                                <IconFont
                                                                    type="Tick"
                                                                    size={16}
                                                                    className={classes.activeIcon}
                                                                    color="var(--byecode-colors-colorPrimary)"
                                                                />
                                                            )}
                                                            {!allSelected && partiallySelected && selectionMode === 'auto-leaf' && (
                                                                <IconFont
                                                                    type="Reduce"
                                                                    size={16}
                                                                    className={classes.activeIcon}
                                                                    color="var(--byecode-colors-colorPrimary)"
                                                                />
                                                            )}
                                                        </Box>

                                                        <Text
                                                            className={classes.label}
                                                            dangerouslySetInnerHTML={{
                                                                __html:
                                                                    keywords === ''
                                                                        ? column.label
                                                                        : column.labelPath?.replaceAll(
                                                                              keywords,
                                                                              `<span style="color: var(--byecode-colors-colorPrimary);">${keywords}</span>`
                                                                          ) ?? ''
                                                            }}
                                                        />
                                                        {(column.children ?? []).length > 0 && (
                                                            <IconFont
                                                                type="ArrowRightSmall"
                                                                size={16}
                                                                color="var(--color-gray-400)"
                                                                className={classes.arrow}
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handlePathSelect(column, index)
                                                                }}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        )
                                    })}

                                    {columns.length === 0 && (
                                        <Empty
                                            description="没有找到数据"
                                            icon="Nodata-8i554976"
                                            style={{ height: '100%' }}
                                            {...emptyProps}
                                        />
                                    )}
                                </Box>
                            </Box>
                        ))}
                        {list.length === 0 && (
                            <Empty
                                className={classes.empty}
                                icon="Nodata-8i554976"
                                description="没有找到数据"
                                style={{ height: '100%' }}
                                {...emptyProps}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
        )
    }
)
